// src/tabs/calendar/Beds24DivergenceModal.jsx — R2-UI CALENDRIER (ADR-256)
//
// Panneau d'action sur une divergence « Bloqué sur Beds24 » : une période est
// indisponible côté Beds24 mais n'est représentée ni par un blocage FBZ ni par
// une réservation active dans l'app. L'admin peut :
//   - « Reprendre dans l'app » (mode=reprendre) → crée un blocage FBZ couvrant
//     le segment réel, SANS push Beds24 (num_avail=0 déjà posé côté Beds24).
//   - « Rouvrir sur Beds24 » (mode=rouvrir, CHEMIN ARGENT) → push réel
//     numAvail=1 vers Beds24 (toutes plateformes). 2e étape de confirmation
//     ROUGE obligatoire (jamais un window.confirm).
//
// Le serveur (handleBeds24ReconciliationResolve, requireAdmin) recompute le
// SEGMENT CONTIGU RÉEL qui contient le span affiché : les dates envoyées ici
// (start/end du span visible) n'ont qu'à être CONTENUES dans le segment. `end`
// = dernière date bloquée INCLUSE (Convention A côté affichage ; le serveur
// convertit en exclusif pour la RPC / le push).
//
// LECTURE-ONLY côté client : aucune décision métier ici, l'auth reste au
// serveur (requireAdmin). Ce composant ne fait qu'orchestrer l'appel + rendre
// les retours lisibles.

import { useEffect, useState } from "react";
import { CloudOff, AlertTriangle, Loader2, Check } from "lucide-react";
import { DS } from "../../lib/ui";
import { supabase } from "../../supabase";

const MOIS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

// Formate une date ISO (YYYY-MM-DD) en français lisible, sans dépendre du
// fuseau (parse manuel, pas de new Date(iso) qui décale à minuit UTC).
function formatDateFR(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return iso || "";
  const [, y, mo, d] = m;
  return `${parseInt(d, 10)} ${MOIS_FR[parseInt(mo, 10) - 1]} ${y}`;
}

// Nb de nuits entre startISO (inclus) et endISO (dernière date INCLUSE).
// 1 seule date bloquée = 1 nuit.
function nightsBetween(startISO, endISO) {
  const a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startISO || "");
  const b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endISO || "");
  if (!a || !b) return null;
  const da = Date.UTC(+a[1], +a[2] - 1, +a[3]);
  const db = Date.UTC(+b[1], +b[2] - 1, +b[3]);
  const diff = Math.round((db - da) / 86400000);
  return diff >= 0 ? diff + 1 : null;
}

// Traduit un couple (status, error) serveur en message FR lisible.
function errorMessageFor(status, json) {
  const code = json?.error || null;
  switch (code) {
    case "no_divergence":
      return "Plus de divergence sur ces dates — rechargez le calendrier.";
    case "overlap_block":
      return "Une période est déjà bloquée dans l'app sur ces dates.";
    case "overlap_reservation":
    case "reservation_overlap":
      return "Une réservation existe sur ces dates.";
    case "booking_exists_on_beds24":
      return "Une réservation active existe côté Beds24 — réouverture refusée.";
    case "missing_room_id":
      return "Logement sans chambre Beds24 configurée — réouverture impossible.";
    case "segment_too_large":
      return `Segment trop long (${json.nights} nuits, max ${json.threshold}) — à traiter depuis Beds24.`;
    case "beds24_check_failed":
      return "Vérification Beds24 impossible — réessayez dans un instant.";
    case "logement_not_configured":
      return "Logement non configuré pour Beds24.";
    case "confirm_required":
      return "Confirmation requise pour rouvrir.";
    case "mode_invalid":
      return "Action invalide.";
    // Codes de validation « ne devrait pas arriver » depuis cette UI — libellé
    // FR générique plutôt qu'un code anglais brut (QA pessimist MINEUR-2).
    case "logement_id_invalid":
    case "dates_invalid":
    case "date_range_inverted":
    case "reason_invalid_or_too_long":
      return "Requête invalide — rechargez le calendrier et réessayez.";
    case "rpc_failed":
    case "rpc_unknown_status":
      return "Erreur serveur lors de la création du blocage — réessayez.";
    default:
      if (status === 401 || status === 403)
        return "Session expirée — reconnectez-vous.";
      if (status === 502) return "Vérification Beds24 impossible — réessayez.";
      if (status === 404) return "Logement non configuré pour Beds24.";
      return json?.message || code || `Erreur serveur (${status}).`;
  }
}

// Message de succès selon le mode + les drapeaux dryRun/queued renvoyés.
// Le serveur renvoie start/end du SEGMENT CONTIGU RÉEL traité (qui peut
// dépasser le span affiché, borné au mois visible) — on cite TOUJOURS ces
// dates réelles dans le message (QA pessimist MAJEUR-1, chemin argent).
function successMessageFor(mode, json) {
  const realSpan =
    json?.start && json?.end
      ? ` Période traitée : ${formatDateFR(json.start)} → ${formatDateFR(json.end)}.`
      : "";
  if (mode === "reprendre") {
    return `Période reprise dans l'app (blocage FBZ créé).${realSpan}`;
  }
  // rouvrir (CHEMIN ARGENT)
  if (json?.dryRun)
    return `Réouverture simulée (mode test) — aucun envoi réel.${realSpan}`;
  if (json?.queued)
    return `Réouverture mise en file — synchro Beds24 sous 15 min.${realSpan}`;
  return `Disponibilité rouverte sur Beds24.${realSpan}`;
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.logementId
 * @param {string} props.logementLabel — nom lisible du logement
 * @param {string} props.startISO — 1re date bloquée (incluse)
 * @param {string} props.endISO — dernière date bloquée (INCLUSE)
 * @param {function} props.onClose — ferme la modale
 * @param {function} props.onResolved — succès : (result) => void (invalidate parent)
 */
export default function Beds24DivergenceModal({
  open,
  logementId,
  logementLabel,
  startISO,
  endISO,
  onClose,
  onResolved,
}) {
  const [reason, setReason] = useState("");
  const [phase, setPhase] = useState("idle"); // 'idle' | 'confirmRouvrir'
  const [submitting, setSubmitting] = useState(false);
  const [submittingMode, setSubmittingMode] = useState(null); // 'reprendre'|'rouvrir'
  const [serverError, setServerError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Reset à chaque (ré)ouverture ou changement de span.
  useEffect(() => {
    if (open) {
      setReason("");
      setPhase("idle");
      setSubmitting(false);
      setSubmittingMode(null);
      setServerError(null);
      setSuccessMsg(null);
    }
  }, [open, logementId, startISO, endISO]);

  // Esc → fermer (sauf pendant un envoi).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const nights = nightsBetween(startISO, endISO);
  const periodLabel =
    startISO === endISO
      ? `${formatDateFR(startISO)} (1 nuit)`
      : `${formatDateFR(startISO)} → ${formatDateFR(endISO)}${nights != null ? ` (${nights} nuit${nights > 1 ? "s" : ""})` : ""}`;

  async function submit(mode) {
    if (submitting) return;
    setSubmitting(true);
    setSubmittingMode(mode);
    setServerError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setServerError("Non authentifié.");
        setSubmitting(false);
        setSubmittingMode(null);
        return;
      }
      const body = {
        action: "beds24_reconciliation_resolve",
        logement_id: logementId,
        start: startISO,
        end: endISO,
        mode,
      };
      const trimmed = (reason || "").trim();
      if (trimmed) body.reason = trimmed.slice(0, 200);
      if (mode === "rouvrir") body.confirm = true;

      const res = await fetch(
        "/api/beds24-mapping?action=beds24_reconciliation_resolve",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        },
      );
      const json = await res.json().catch(() => ({}));
      // QA pessimist MINEUR-1 : un 200 peut porter ok:false SANS queued ni
      // dryRun (échec push non mis en file) — ce n'est PAS un succès.
      // 2026-09-15 : un REFUS Beds24 (json.verdict) est un échec même s'il est
      // « mis en file » — le rejeu échouera tant que la cause n'est pas
      // corrigée, et la période RESTE fermée. Ne jamais l'annoncer « en file ».
      const softFail =
        res.ok && json?.ok === false && (!!json?.verdict || (!json?.queued && !json?.dryRun));
      if (!res.ok || softFail) {
        // Revue adverse #1427 (MAJEUR 2) : le verdict se lit EN PREMIER — il
        // arrive en 200 (softFail vrai), la branche était morte derrière.
        setServerError(
          json?.verdict
            ? `Beds24 a REFUSÉ la réouverture (${json.verdict}) : ${json.error || "sans motif"}. La période reste FERMÉE à la vente chez Beds24 ; l'envoi est en file mais échouera tant que la cause n'est pas corrigée — vérifiez le calendrier Beds24.`
            : softFail
              ? "La réouverture n'a pas pu être envoyée à Beds24 — réessayez."
              : errorMessageFor(res.status, json),
        );
        setSubmitting(false);
        setSubmittingMode(null);
        return;
      }
      // Succès : message puis invalidation parent. On garde la modale affichée
      // un court instant avec le message de succès, puis on remonte au parent.
      setSuccessMsg(successMessageFor(mode, json));
      setSubmitting(false);
      setSubmittingMode(null);
      if (onResolved) onResolved({ mode, ...json });
    } catch (e) {
      // Jamais de catch muet — on affiche l'erreur à l'admin.
      console.error("[Beds24DivergenceModal] resolve failed", e);
      setServerError(e?.message || "Erreur réseau — réessayez.");
      setSubmitting(false);
      setSubmittingMode(null);
    }
  }

  return (
    <div
      onClick={() => { if (!submitting) onClose(); }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        className="fbz-modal-sheet"
        data-testid="beds24-divergence-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: DS.card,
          borderRadius: DS.radiusCard,
          maxWidth: 460,
          width: "100%",
          padding: 22,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* En-tête */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: DS.fontDisplay,
              fontSize: 18,
              fontWeight: 600,
              color: DS.marineDark,
            }}
          >
            <CloudOff size={18} strokeWidth={2.2} /> Divergence Beds24
          </div>
          <div style={{ fontSize: 13, color: DS.marineDark, marginTop: 8, fontWeight: 600 }}>
            {logementLabel || logementId} · {periodLabel}
          </div>
          <div style={{ fontSize: 12, color: DS.textSecondary, marginTop: 6, lineHeight: 1.5 }}>
            Cette période est indisponible côté Beds24 mais n'est représentée
            ni par un blocage ni par une réservation dans l'app. Choisissez
            comment rapprocher les deux.
          </div>
        </div>

        {/* Succès */}
        {successMsg && (
          <div
            data-testid="beds24-divergence-success"
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              padding: 10,
              background: DS.successBg,
              color: DS.successText,
              border: `1px solid ${DS.success}55`,
              borderRadius: 8,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            <Check size={14} strokeWidth={2.4} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Erreur serveur */}
        {serverError && (
          <div
            data-testid="beds24-divergence-error"
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              padding: 10,
              background: DS.dangerBg,
              color: DS.dangerText,
              border: `1px solid ${DS.danger}44`,
              borderRadius: 8,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            <AlertTriangle size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{serverError}</span>
          </div>
        )}

        {successMsg ? (
          // État terminal : un seul bouton Fermer (le parent a déjà rafraîchi).
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="fbz-press"
              onClick={onClose}
              style={{
                background: DS.marine,
                color: "#fff",
                border: "none",
                borderRadius: DS.radiusControl,
                padding: "8px 16px",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Fermer
            </button>
          </div>
        ) : phase === "confirmRouvrir" ? (
          // 2e étape : avertissement ROUGE avant réouverture réelle.
          <div>
            <div
              data-testid="beds24-divergence-rouvrir-warning"
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: 12,
                background: DS.dangerBg,
                color: DS.dangerText,
                border: `1px solid ${DS.danger}66`,
                borderRadius: 8,
                fontSize: 12,
                lineHeight: 1.5,
                marginBottom: 14,
              }}
            >
              <AlertTriangle size={16} strokeWidth={2.4} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong>Cette action ROUVRE réellement la disponibilité sur
                Beds24</strong> (toutes plateformes). À n'utiliser que si ce
                blocage est périmé. Une réservation peut arriver immédiatement
                sur ces dates. Elle porte sur <strong>toute la période de
                blocage contiguë</strong>, qui peut dépasser le mois affiché —
                les dates réellement traitées seront confirmées après l'action.
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                className="fbz-press"
                onClick={() => setPhase("idle")}
                disabled={submitting}
                style={{
                  background: DS.card,
                  color: DS.marine,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                className="fbz-press"
                data-testid="beds24-divergence-confirm-rouvrir"
                onClick={() => submit("rouvrir")}
                disabled={submitting}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: DS.danger,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting && submittingMode === "rouvrir"
                  ? <><Loader2 size={13} /> Réouverture…</>
                  : "Confirmer la réouverture"}
              </button>
            </div>
          </div>
        ) : (
          // 1re étape : raison + choix reprendre / rouvrir.
          <div>
            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 700,
                  color: DS.navy,
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Raison (facultatif)
              </label>
              <input
                type="text"
                data-testid="beds24-divergence-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submitting}
                placeholder="ex : blocage proprio périmé"
                maxLength={200}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: `0.5px solid ${DS.borderHover}`,
                  fontSize: 13,
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                className="fbz-press"
                data-testid="beds24-divergence-reprendre"
                onClick={() => submit("reprendre")}
                disabled={submitting}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  background: DS.marine,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting && submittingMode === "reprendre"
                  ? <><Loader2 size={14} /> Reprise…</>
                  : "Reprendre dans l'app"}
              </button>
              <div style={{ fontSize: 11, color: DS.textSecondary, marginTop: -2, marginBottom: 4 }}>
                Recommandé : crée un blocage FBZ sur ces dates, sans rien
                renvoyer vers Beds24.
              </div>

              <button
                type="button"
                className="fbz-press"
                data-testid="beds24-divergence-rouvrir"
                onClick={() => { setServerError(null); setPhase("confirmRouvrir"); }}
                disabled={submitting}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  background: "transparent",
                  color: DS.dangerText,
                  border: `1px solid ${DS.danger}66`,
                  borderRadius: DS.radiusControl,
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                Rouvrir sur Beds24
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                className="fbz-press"
                onClick={onClose}
                disabled={submitting}
                style={{
                  background: DS.card,
                  color: DS.marine,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
